import { ActionIcon, Alert, Button, Group, Loader, Slider, Stack, Text, Title, Tooltip, VisuallyHidden } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Link, useBlocker, useParams } from "@tanstack/react-router";
import { ChevronLeft, Keyboard, Magnet, Redo2, Save, ScanEye, SeparatorVertical, Undo2 } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useReducer, useRef, useState, type ReactNode } from "react";

import { fetchSignedFile, saveBlob } from "../../api/download";
import type { EffectKind, TransitionKind } from "../../api/generated/model";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { useIdempotencyKey } from "../../platform/idempotency";
import { useMembership } from "../../platform/membership";
import { canEditContent } from "../../platform/permissions";
import { readPref, writePref } from "../../platform/prefs";
import { projectsIn } from "./exits";
import { useSchemeSwitch } from "../../platform/shell/UserMenu";
import { ConfirmModal } from "../../ui/ConfirmModal";
import { Logo } from "../../ui/Logo";
import { problems } from "./checks";
import { ExportPopover } from "./ExportPopover";
import { SERVER_PRESET, type ExportSettings } from "./engine/presets";
import { resolveProject } from "./engine/project";
import { ExportStatus, LocalExportStatus } from "./ExportStatus";
import { BottomSheet } from "./BottomSheet";
import { ExportBell, ExportList, ExportSaver, bellName, bellState, useExportSaves, type ExportTask } from "./exports";
import { canMakeHere, useBrowserSaves } from "./browser-save";
import { Inspector } from "./Inspector";
import { useLocalExports } from "./local-export";
import { MediaBin, type BinSheet } from "./MediaBin";
import { PHONE, phoneActions, sheetTitle, type SheetHeight, type SheetName } from "./phone";
import { AtPlayhead, createPlayhead, usePlayheadWatch } from "./playhead";
import { PhoneActions, PhoneMenu, PhoneTabs, PhoneTransport } from "./PhoneBars";
import type { AssistantEditor } from "./assistant/use-assistant";
import { sourceKey, useProjectMedia, useSourceMedia } from "./media";
import type { CanvasCommand } from "./CanvasTools";
import { Preview, fullScreenSupported, type CanvasControls } from "./Preview";
import { useAssistMedia } from "./assistant/assist-media";
import type { Said } from "./captions/Transcript";
import { useGenerateCaptions } from "./captions/use-generate-captions";
import {
  PROJECT_NAME_MAX,
  isStaleVersion,
  liveFrame,
  sameContent,
  useProject,
  useSaveProject,
  useStartRender,
  type EditorProject,
  type SaveRequest,
} from "./project";
import { useAutosave } from "./autosave";
import { draftOf, editDraft } from "./draft";
import { useProjectSync } from "./use-project-sync";
import { useProjectTabs } from "./project-channel";
import {
  clipsChosen,
  everything,
  isChosen,
  only,
  stillSelected,
  toggled,
  type Selection,
} from "./selection";
import { draggedText, presetStyle, type TextPreset } from "./text-presets";
import { ADDED_TEXT, ADDED_TEXT_MS, addedStyle, type TextStyle } from "./text-style";
import {
  MAX_EFFECTS,
  MAX_TITLES,
  MAX_TRACKS,
  MIN_SPAN_MS,
  captionsDropped,
  countOf,
  fullKinds,
  mediaClips,
  nearestJunction,
  newId,
  showsAt,
  totalMs,
  type AudioClip,
  type CaptionClip,
  type Clip,
  type ImageClip,
  type TextClip,
  type VideoClip,
  type ClipSource,
  type Junction,
  type MediaKind,
  type TimelineEdit,
} from "./timeline";
import { commandOf, onMac, type Command } from "./keys";
import { captionText } from "./captions/words";
import { formatClock } from "./time";
import { useRemoveBackground } from "./use-remove-background";
import { SaveToProject } from "./SaveToProject";
import { ShortcutsModal } from "./ShortcutsModal";
import { TimelineMenu, type MenuAction, type MenuPlace } from "./TimelineMenu";
import { TIMELINE_HEIGHT, TimelineResizer } from "./TimelineResizer";
import { DEFAULT_ZOOM, TimelineToolbar, zoomBy } from "./TimelineToolbar";
import { PX_PER_SECOND, Tracks } from "./Tracks";
import classes from "./editor.module.css";

/** Where Space keeps its own meaning: typing, or pressing a control other than a block. */
const SPACE_TAKEN = 'input, textarea, select, button, a, [contenteditable="true"], [role="tab"]';

/**
 * Where a key belongs to the text being typed. A slider types none: the ruler leaves the focus on
 * the Playhead's, and M, Q and W still act there (spec beats.md); its own arrows are no command.
 */
const TYPING = 'input:not([type="range"]), textarea, select, [contenteditable="true"]';

/** Space plays and pauses, on the page or on a timeline block, but never inside a dialog. */
function spaceIsOurs(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return true;
  if (target.closest('[role="dialog"]') !== null) return false;
  if (target.closest("[data-block]") !== null) return true;
  return target.closest(SPACE_TAKEN) === null;
}

/**
 * What a move that took off captions says (D7, spec captions-move-with-clip): each by its words, or
 * by its time with none. Live's toasts are said in a line here (web/AGENTS.md: no toasts).
 */
function droppedText(dropped: readonly CaptionClip[]): string {
  const names = dropped.map((caption) => {
    const text = captionText(caption.words).replace(/\s+/g, " ").trim();
    if (text === "") return `at ${formatClock(caption.startMs)}`;
    return `“${text.length > 40 ? `${text.slice(0, 39)}…` : text}”`;
  });
  const [first] = names;
  if (names.length === 1 && first !== undefined) {
    return `Caption ${first} was taken off: it would have lain over another caption. Undo brings it back.`;
  }
  const shown = names.length > 3 ? [...names.slice(0, 2), `${names.length - 2} more`] : names;
  return `Captions ${shown.slice(0, -1).join(", ")} and ${shown.at(-1) ?? ""} were taken off: they would have lain over other captions. Undo brings them back.`;
}

/** One project in the editor (E1): the URL's project, in the URL's brand. */
export function EditorScreen() {
  const { teamId, brandId, projectId } = useParams({
    from: "/signed-in/teams/$teamId/brands/$brandId/editor/$projectId",
  });
  const project = useProject(teamId, brandId, projectId);
  // The project's media goes out beside its own read (spec editor/project-media.md).
  useProjectMedia(teamId, brandId, projectId);
  // Once read, the editor stays on its draft when a read again fails (a conflict's, spec
  // conflict-keeps-draft): only a project never read shows the error here.
  if (project.data === undefined) {
    if (project.isError) {
      return (
        <Stack p="xl">
          <ErrorAlert error={project.error} />
        </Stack>
      );
    }
    return <Loader size="sm" m="xl" aria-label="Loading the project" />;
  }
  return <OpenedEditor key={project.data.id} teamId={teamId} brandId={brandId} project={project.data} />;
}

/**
 * The editor on the project as first read. Its draft starts from that read and takes the project's
 * later saves in through its sync (spec live-sync-web.md), so a new read of the project, each save's
 * own answer among them, renders no editor: only the read's name reaches it (spec save-renders.md).
 */
function OpenedEditor({ teamId, brandId, project }: { teamId: string; brandId: string; project: EditorProject }) {
  const [opened] = useState(project);
  return <Editor teamId={teamId} brandId={brandId} project={opened} readName={project.name} />;
}

interface EditorProps {
  teamId: string;
  brandId: string;
  /** The project as first read: its id, its creator and the draft's start. */
  project: EditorProject;
  /** The name of the project's latest read, which a Save's file and the leave dialog show. */
  readName: string;
}

const Editor = memo(function Editor({ teamId, brandId, project, readName }: EditorProps) {
  // The draft with its undo, what the server holds of it, and a newer save it could not take in
  // (spec live-sync-web.md), in one reducer.
  const [drafted, act] = useReducer(
    editDraft,
    { version: project.version, timeline: project.timeline, name: project.name },
    draftOf,
  );
  const history = drafted.history;
  const draft = history.present;
  // When this tab last changed its draft itself: the autosave rests from it, so a save taken in
  // from elsewhere never restarts the rest (spec live-sync-web.md).
  const [editedAt, setEditedAt] = useState(0);
  const edit = useCallback((change: TimelineEdit) => {
    const at = Date.now();
    setEditedAt(at);
    act({ type: "edit", edit: change, at });
  }, []);
  const step = (action: "undo" | "redo") => () => {
    setEditedAt(Date.now());
    act({ type: action });
  };
  const undo = history.past.length > 0 ? step("undo") : undefined;
  const redo = history.future.length > 0 ? step("redo") : undefined;
  // A move that took off a caption its clip carried (D7, spec captions-move-with-clip), said until
  // the next step: its undo brings the caption back.
  const lastStep = history.past.at(-1);
  const dropped = useMemo(() => (lastStep === undefined ? [] : captionsDropped(lastStep, draft)), [lastStep, draft]);
  // What the server holds: the version a save must name, and the timeline and name it saved.
  const saved = drafted.held;
  // Live's name in the header, as typed (decision 21): it saves with the draft, never as an undo
  // step; an empty one is not saved. Null while it is the saved name, which another tab's rename
  // then changes (spec live-sync-web.md).
  const [typed, setTyped] = useState<string | null>(null);
  const name = typed ?? saved.name;
  const savedName = name.trim() === "" ? saved.name : name.trim();
  const [chosen, setChosen] = useState<Selection | null>(null);
  // The playhead: a store, so a change of it renders only what shows the time (spec seek-renders.md).
  const [playhead] = useState(() => createPlayhead(0));
  const [playing, setPlaying] = useState(false);
  // A newer saved version the draft could not take in (spec live-sync-web.md: a true conflict):
  // the draft and its undo stay until Keep mine or Take theirs (spec conflict-keeps-draft).
  const theirs = drafted.conflict;
  // This editor's exports, newest first, and the saves of their files to the computer.
  const [exports, setExports] = useState<readonly ExportTask[]>([]);
  // Exports made in this browser (E2.1b), and which kind ran last, for the line under the header.
  const local = useLocalExports();
  const [newest, setNewest] = useState<"local" | "server">("local");
  const fileSaves = useExportSaves();
  // Saves whose file this tab makes (spec fast-save); one the server takes over is its render's
  // row from then on.
  const browserSaves = useBrowserSaves({
    teamId,
    brandId,
    projectId: project.id,
    onHandedOver: (from, to) =>
      setExports((current) =>
        current.map((task) => (task.jobId === from ? { ...task, jobId: to.jobId, made: to.madeIn, handedOver: true } : task)),
      ),
    onFailed: fileSaves.fail,
  });
  // Live's answer to an export with nothing to export, until the next one.
  const [nothing, setNothing] = useState(false);
  // Why the browser cannot make the chosen export (live: "Browser Not Supported"), until the next.
  const [refused, setRefused] = useState<string | null>(null);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  // Live's panel toggle (decision 21): the timeline folded to its toolbar and a seek slider, for
  // this page only, as live's.
  const [timelineFolded, setTimelineFolded] = useState(false);
  // Live's resizable timeline: its height, dragged on its top edge.
  const [timelineHeight, setTimelineHeight] = useState(TIMELINE_HEIGHT);
  // The timeline's switches (spec capcut-shortcuts.md), kept in this browser: live's "Attach" (N)
  // and the preview axis (S), with the axis's time while the pointer is over the tracks.
  const [snapping, setSnapping] = useState(() => readPref("timelineSnapping"));
  const [previewAxis, setPreviewAxis] = useState(() => readPref("previewAxis"));
  const [hoverMs, setHoverMs] = useState<number | null>(null);
  const toggleSnapping = () => {
    writePref("timelineSnapping", !snapping);
    setSnapping(!snapping);
  };
  const togglePreviewAxis = () => {
    writePref("previewAxis", !previewAxis);
    setPreviewAxis(!previewAxis);
  };
  // The phone layout (spec phone-layout.md): its one query, read at the first render so a phone never
  // draws the desktop first; the sheet open and its height; the bell's read rows, kept while its
  // sheet is shut.
  const phone = useMediaQuery(PHONE, false, { getInitialValueInEffect: false }) === true;
  const [sheet, setSheet] = useState<SheetName | null>(null);
  const [sheetHeight, setSheetHeight] = useState<SheetHeight>("half");
  const openSheet = (next: SheetName | null) => {
    setSheet(next);
    setSheetHeight("half");
  };
  const closeSheet = useCallback(() => setSheet(null), []);
  // The phone's Speed opens the Parameters at its Speed section: each press brings it into view.
  const [speedShown, setSpeedShown] = useState(0);
  const [bellRead, setBellRead] = useState<ReadonlySet<string>>(new Set());
  const scheme = useSchemeSwitch();
  // The canvas's view, which the canvas keys reach through the preview.
  const canvas = useRef<CanvasControls | null>(null);
  const onCanvas = (command: CanvasCommand) => () => canvas.current?.run(command);
  // The studio covers the app's frame, which goes inert: focus starts on its name, inside it.
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => heading.current?.focus(), []);
  const save = useSaveProject(teamId, brandId, project.id);
  const startExport = useStartRender(teamId, brandId, project.id);
  const projectQuery = useProject(teamId, brandId, project.id);
  const exportKey = useIdempotencyKey();
  // Every member reads a project; its creator, an admin or the owner changes it (D6). A hint
  // only: the API refuses a change from anyone else.
  const me = useMembership(teamId);
  const readOnly = me === undefined || !canEditContent(me.role, project.createdBy, me.userId);
  const change = readOnly ? () => undefined : edit;
  // Remove background's runs, one a clip, kept while another is chosen (spec remove-background.md).
  const background = useRemoveBackground({ teamId, brandId, onEdit: change });

  const selection = stillSelected(chosen, draft);
  // A chosen caption opens live's Captions tab (#239).
  const chosenCaption =
    draft.clips.find((clip) => clip.kind === "caption" && (selection?.ids ?? []).includes(clip.id))?.id ?? null;
  const media = useSourceMedia(
    teamId,
    brandId,
    project.id,
    mediaClips(draft).map((clip) => clip.source),
  );
  const total = totalMs(draft);
  const dirty = !sameContent(draft, saved.timeline) || savedName !== saved.name;
  const blocked = problems(draft);
  const busy = save.isPending || startExport.isPending;
  // Leaving with unsaved changes asks first, in the app and when the tab closes.
  const leaving = useBlocker({
    shouldBlockFn: () => dirty,
    enableBeforeUnload: () => dirty,
    withResolver: true,
  });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== " " || event.repeat || !spaceIsOurs(event.target)) return;
      if (draft.clips.length === 0) return;
      event.preventDefault();
      if (!playing && playhead.get() >= total) playhead.set(0);
      setPlaying(!playing);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [draft.clips.length, playing, playhead, total]);

  const onPlayhead = playhead.set;
  const onPlaying = useCallback((next: boolean) => setPlaying(next), []);

  // The saves of the project made elsewhere, read and taken in as they come (spec live-sync-web.md):
  // the stream's word, a stream live again or down, and the asks below (another tab, a 409).
  const sync = useProjectSync({
    teamId,
    brandId,
    projectId: project.id,
    held: saved,
    readWhole: async () => {
      const fresh = await projectQuery.refetch();
      if (fresh.isError || fresh.data === undefined) return null;
      return { version: fresh.data.version, timeline: fresh.data.timeline, name: fresh.data.name };
    },
    onNewer: (versions) => act({ type: "sync", versions }),
  });
  // While a newer version is read, or waits for Keep mine or Take theirs, nothing else saves.
  const held = theirs !== null || sync.behind;

  // This browser's other tabs on the project: one that saves a newer version is read at once.
  const tabs = useProjectTabs(project.id, (version) => {
    if (version > saved.version) void sync.catchUp(true);
  });

  /**
   * Saves the draft naming the saved version, or theirs for Keep mine; the version the server now
   * holds, or `undefined` when it failed or a newer version waits for Keep mine or Take theirs.
   * A 409 reads what changed and takes it in; the draft then saves itself again.
   */
  const saveDraft = async (over?: number): Promise<number | undefined> => {
    if (over === undefined && held) return undefined;
    const request: SaveRequest = {
      name: savedName,
      aspectRatio: draft.aspect,
      version: over ?? saved.version,
      timeline: draft,
    };
    // A read may bring this save back before its answer: the draft knows it as its own.
    act({ type: "sending", sent: { from: request.version, timeline: draft, name: request.name } });
    try {
      const next = await save.save(request);
      act({ type: "saved", held: { version: next.version, timeline: draft, name: request.name } });
      // A name typed and now saved follows the saved name again, which another tab may change.
      setTyped((current) => (current !== null && current.trim() === request.name ? null : current));
      tabs.announce(next.version);
      return next.version;
    } catch (error) {
      if (isStaleVersion(error)) await sync.catchUp(true);
      return undefined;
    }
  };

  /**
   * Take theirs: the newer version as one undo step (`restore`, the assistant's Revert's edit), so
   * Undo brings the draft back, which then saves itself over it as any edit does.
   */
  const takeTheirs = () => {
    if (theirs === null) return;
    act({ type: "takeTheirs", at: Date.now() });
    setTyped(null);
  };

  /** The saved version a render or the assistant names, the draft saved first when it changed. */
  const versionToName = async (): Promise<number | undefined> => (dirty ? saveDraft() : held ? undefined : saved.version);

  // Live keeps the project as it is edited: the draft saves itself once this tab's own edits rest
  // (a save taken in never restarts the rest), but not while a newer version is read or waits.
  // Keyed on the saved version too, so a draft tried at an older version is tried again at the newer.
  const content = useMemo(() => ({ draft, name: savedName, version: saved.version }), [draft, savedName, saved.version]);
  useAutosave(content, dirty && blocked.length === 0 && !busy && !readOnly && !held, saveDraft, editedAt);

  // Live's AI assistant (#242): it reads the saved project, so a message saves the draft first,
  // and edits the draft with the editor's own edits. A file's name is what it is shown as; the
  // files it may add are the media bin's newest (G11 PR 2).
  const offeredMedia = useAssistMedia(teamId, brandId);
  // The editor's one caption run (#239): the media panel's Generate and the assistant's Auto
  // Captions (#242) start it; its line is kept while another tab is open.
  const [captionsSaid, setCaptionsSaid] = useState<Said | null>(null);
  const captions = useGenerateCaptions({
    teamId,
    brandId,
    timeline: draft,
    aspectRatio: draft.aspect,
    onEdit: change,
    onSay: setCaptionsSaid,
  });
  const assistantEditor: AssistantEditor = {
    timeline: draft,
    savedVersion: saved.version,
    dirty,
    mark: history,
    save: async () => (dirty && blocked.length > 0 ? undefined : versionToName()),
    labels: Object.fromEntries(
      mediaClips(draft).flatMap((clip) => {
        const name = media.get(sourceKey(clip.source))?.name;
        return name === undefined ? [] : [[clip.id, name]];
      }),
    ),
    media: offeredMedia,
    context: {
      sourceMs: (clip) =>
        clip.kind === "video" || clip.kind === "audio" ? (media.get(sourceKey(clip.source))?.durationMs ?? null) : null,
      titleStyle: addedStyle(liveFrame(draft.aspect)),
      newId,
      captionsBusy: captions.busy,
    },
    captions: (from) => captions.generate(from, "assistant"),
    onEdit: change,
  };

  // Live's Save: "Save to project", then the video is rendered into the chosen folder: in this
  // tab when it can make the server's file, else on the server (spec fast-save). A failed Save's
  // Try again in the bell runs it again into the same folder, on the server (spec save-failures).
  const [saveOpen, setSaveOpen] = useState(false);
  const canSave = draft.clips.length > 0 && blocked.length === 0 && !busy;
  const saveToProject = async (folderId: string | null, onServer = false) => {
    setSaveOpen(false);
    startExport.reset();
    if (draft.clips.length === 0 || blocked.length > 0) return;
    // The draft as it is saved now: the file made here is that version's.
    const made = draft;
    const version = await versionToName();
    if (version === undefined) return;
    const known = mediaClips(made).every((clip) => media.get(sourceKey(clip.source))?.url != null);
    const preset = SERVER_PRESET.video === null ? {} : { short: SERVER_PRESET.video.short, fps: SERVER_PRESET.video.fps };
    const resolved = known ? resolveProject(made, made.aspect, media, preset) : null;
    const here = !onServer && resolved !== null && (await canMakeHere(resolved));
    const request = { version, folder: folderId, here };
    const key = exportKey.keyFor(request);
    try {
      const started = await startExport.start(version, key, folderId, here ? "browser" : "server");
      setExports((current) => [
        {
          jobId: started.jobId,
          startedAt: Date.now(),
          kind: "save",
          name: `${readName} v${version}.mp4`,
          folderId,
          made: started.madeIn,
        },
        ...current,
      ]);
      setNewest("server");
      if (started.madeIn === "browser" && resolved !== null) void browserSaves.make(started.jobId, resolved);
    } catch (error) {
      exportKey.failed(key, request, error);
      if (isStaleVersion(error)) await sync.catchUp(true);
    }
  };

  // Live's Snapshot: the frame at the playhead as `frame-{N}.png`, at the project's size; a frame
  // the browser cannot draw is logged, as live's, and says nothing (spec editor/media-panel.md).
  const snapshot = async () => {
    const atMs = playhead.get();
    try {
      const engine = await import("./engine/snapshot");
      const resolved = resolveProject(draft, draft.aspect, media);
      const file = await engine.snapshotFile(resolved, atMs, fetchSignedFile);
      saveBlob(file, engine.snapshotName(resolved, atMs));
    } catch (error) {
      console.error("Failed to capture frame:", error);
    }
  };

  const exportDraft = async (settings: ExportSettings) => {
    startExport.reset();
    setRefused(null);
    setNothing(draft.clips.length === 0);
    // Live lets Download be pressed on an empty project and answers "No clips to export".
    if (draft.clips.length === 0 || blocked.length > 0) return;
    // As live's: the browser makes the file from the draft as it is, saving nothing first, once
    // every clip's file is known, at the settings' size and rate (the chosen resolution sets the
    // file's size: the lead's decision on #243).
    const known = mediaClips(draft).every(
      (clip) => media.get(sourceKey(clip.source))?.url != null,
    );
    const options = settings.video === null ? {} : { short: settings.video.short, fps: settings.video.fps };
    const result = known
      ? await local.start(resolveProject(draft, draft.aspect, media, options), settings)
      : { kind: "refused" as const, reason: "A clip's file is still loading; try again in a moment." };
    if (result.kind === "started") {
      setNewest("local");
      return;
    }
    // A browser that cannot make it keeps the server's render, which makes E1's HD 720p MP4
    // only; any other choice says why it cannot be made here.
    if (!sameSettings(settings, SERVER_PRESET)) {
      setRefused(result.reason);
      return;
    }
    const version = await versionToName();
    if (version === undefined) return;
    const request = { version };
    const key = exportKey.keyFor(request);
    try {
      const started = await startExport.start(version, key, null, "server", "download");
      setExports((current) => [{ jobId: started.jobId, startedAt: Date.now() }, ...current]);
      setNewest("server");
    } catch (error) {
      exportKey.failed(key, request, error);
      if (isStaleVersion(error)) await sync.catchUp(true);
    }
  };

  const addClip = (kind: MediaKind, source: ClipSource, sourceMs: number | null) => {
    const id = newId();
    edit({ type: "addClip", id, kind, source, sourceMs, newTrackId: newId() });
    setChosen(only(id));
  };

  /** A text added to the timeline, chosen. */
  const putTitle = (text: string, style: TextStyle, startMs: number, endMs: number) => {
    const id = newId();
    edit({ type: "addTitle", id, text, startMs, endMs, style, newTrackId: newId() });
    setChosen(only(id));
  };
  /**
   * Live's "Add Text" at the playhead: "New text element" in Roboto Bold 48, white, centred in
   * the frame; or one of live's presets, its word in its face at live's place. For live's 5 s,
   * within the video when it can be.
   */
  const addTitle = (preset: TextPreset | null) => {
    const startMs = Math.max(0, Math.min(playhead.get(), total - MIN_SPAN_MS));
    const endMs = Math.max(startMs + MIN_SPAN_MS, Math.min(startMs + ADDED_TEXT_MS, total));
    const style = preset === null ? addedStyle(liveFrame(draft.aspect)) : presetStyle(preset);
    putTitle(preset?.text ?? ADDED_TEXT, style, startMs, endMs);
  };
  /**
   * Live's text tile or "Add Text" let go on the timeline (issue 241): live's 5 s from the
   * pointer's time, its preset's text (Add Text's is live's first preset, "This is a text clip").
   */
  const dropTitle = (data: string, atMs: number) => {
    const dropped = draggedText(data);
    if (dropped === null) return;
    const startMs = Math.max(0, Math.round(atMs));
    putTitle(dropped.text, presetStyle(dropped.preset), startMs, startMs + ADDED_TEXT_MS);
  };

  // Live's transitions (#240). A tile puts its kind on the cut nearest the playhead (nothing
  // happens with no cut, as on live); a drag on the cut it is let go by; "+" on a cut a Fade,
  // and opens the Transitions tab. None of them chooses the transition: its mark does.
  const [transitionsOpened, setTransitionsOpened] = useState(0);
  const [droppingTransition, setDroppingTransition] = useState(false);
  const putTransition = (cut: Junction, kind: TransitionKind) =>
    edit({ type: "setTransition", id: newId(), fromId: cut.fromId, toId: cut.toId, kind });
  const applyTransition = (kind: TransitionKind) => {
    const cut = nearestJunction(draft, playhead.get());
    if (cut !== null) putTransition(cut, kind);
  };
  const addTransitionAt = (cut: Junction) => {
    putTransition(cut, "fade");
    setTransitionsOpened((count) => count + 1);
    // On a phone the Transitions panel is a sheet: live's "+" opens it.
    if (phone) openSheet("transitions");
  };

  // Live's effects (#265): a tile, clicked or dropped anywhere on the timeline, adds its effect
  // from 0 to 5 s on a new top track. Live's tab does not choose it.
  const addEffect = (effect: EffectKind) =>
    edit({ type: "addEffect", id: newId(), effect, newTrackId: newId() });

  const saveProblem = save.isError && !isStaleVersion(save.error) ? save.error : null;
  const exportProblem =
    startExport.isError && !isStaleVersion(startExport.error) ? startExport.error : null;

  const togglePlay = () => {
    if (!playing && playhead.get() >= total) playhead.set(0);
    setPlaying(!playing);
  };
  const seek = (ms: number) => {
    setPlaying(false);
    playhead.set(ms);
  };
  const selectedClip = draft.clips.find((clip) => clip.id === selection?.id);
  // Every chosen clip (live's ⇧-click and ⌘A), the one the Parameters show first.
  const selectedClips = (selection?.ids ?? []).flatMap((id) => {
    const clip = draft.clips.find((each) => each.id === id);
    return clip === undefined ? [] : [clip];
  });
  // Live writes ⌘ on a Mac; other systems read Ctrl.
  const mac = onMac(navigator.userAgent);
  // What the member may change of the selected clip: nothing when they may not change the
  // project, or while the clip is locked (live's Lock); and of all the chosen, the unlocked ones.
  const changing = readOnly || selectedClip?.locked !== false ? undefined : selectedClip;
  const changeable = readOnly ? [] : selectedClips.filter((clip) => !clip.locked);
  /** Edits of several clips as one step (one edit alone as itself), or nothing to do. */
  const editAll = (edits: readonly TimelineEdit[]) => {
    const [first] = edits;
    if (first === undefined) return undefined;
    return () => edit(edits.length === 1 ? first : { type: "many", edits });
  };
  // A chosen transition is chosen alone; Delete takes it away (live's "None").
  const chosenTransition = draft.transitions.find((each) => each.id === selection?.id);
  const removeSelected =
    chosenTransition !== undefined
      ? readOnly
        ? undefined
        : () => edit({ type: "removeTransition", id: chosenTransition.id })
      : editAll(changeable.map((clip) => ({ type: "removeClip", id: clip.id }) as const));
  // The main track magnet's edits (spec magnet-ripple): ⇧⌫ on every chosen clip, Q and W on each
  // the playhead is inside, and the toolbar's switch.
  const rippleSelected =
    chosenTransition === undefined
      ? editAll(changeable.map((clip) => ({ type: "removeClip", id: clip.id, ripple: true }) as const))
      : undefined;
  // An edit at the playhead: offered while it would change something there, and made at the
  // playhead's time when it runs, since the screen does not render at each seek (seek-renders.md).
  const editAtPlayhead = (editsAt: (ms: number) => readonly TimelineEdit[]) =>
    editsAt(playhead.get()).length === 0 ? undefined : () => editAll(editsAt(playhead.get()))?.();
  const trimSelected = (side: "start" | "end") =>
    editAtPlayhead((ms) =>
      changeable
        .filter((clip) => showsAt(clip, ms))
        .map((clip) => ({ type: "trimToPlayhead", id: clip.id, atMs: ms, side }) as const),
    );
  // M (spec beats.md, D6): with a sound among the chosen, a beat on each chosen sound the playhead
  // is over, as one step; with none, item 20's marker, not built yet.
  const markSelected = selectedClips.some((clip) => clip.kind === "audio")
    ? editAtPlayhead((ms) =>
        changeable
          .filter((clip) => clip.kind === "audio" && showsAt(clip, ms))
          .map((clip) => ({ type: "toggleBeat", id: clip.id, atMs: ms }) as const),
      )
    : undefined;
  const magnetOn = draft.tracks.some((track) => track.magnet);
  const toggleMagnet = readOnly ? undefined : () => edit({ type: "setMagnet", on: !magnetOn, newTrackId: newId() });
  // Live's Split cuts the selected clip at the playhead, when the playhead is inside it.
  const splitsAt = (ms: number) =>
    changing !== undefined &&
    showsAt(changing, ms) &&
    ms - changing.startMs >= MIN_SPAN_MS &&
    changing.endMs - ms >= MIN_SPAN_MS;
  const splitSelected =
    changing === undefined || !splitsAt(playhead.get())
      ? undefined
      : () => {
          const atMs = playhead.get();
          if (!splitsAt(atMs)) return;
          const id = newId();
          edit({ type: "split", id: changing.id, atMs, newId: id });
          setChosen(only(id));
        };
  // What is offered at the playhead (Split, Q and W, M): the screen renders again when it changes.
  usePlayheadWatch(
    playhead,
    (ms) => `${splitsAt(ms)} ${changeable.filter((clip) => showsAt(clip, ms)).map((clip) => clip.id).join(" ")}`,
  );
  // One caption track (#239, spec captions.md decision 8): a caption is not duplicated, copied
  // or cut to the clipboard.
  const holdsCaption = selectedClips.some((clip) => clip.kind === "caption");
  // A copy of a locked clip is not locked: the lock keeps that clip as it is. Every chosen clip
  // is copied, and the copies are chosen.
  const duplicateSelected =
    readOnly || selectedClips.length === 0 || holdsCaption
      ? undefined
      : () => {
          const copies = selectedClips.map((clip) => ({ clip, id: newId() }));
          edit({
            type: "many",
            edits: copies.map(({ clip, id }) => ({
              type: "duplicate",
              id: clip.id,
              newId: id,
              newTrackId: newId(),
            })),
          });
          const ids = copies.map((copy) => copy.id);
          setChosen({ id: ids[0]!, ids });
        };
  // Live's Separate Audio, on the selected video: its sound becomes a clip of its own, which is
  // then chosen, as live's is.
  const separateSelected =
    changing?.kind !== "video"
      ? undefined
      : () => {
          const id = newId();
          edit({ type: "separateAudio", id: changing.id, newId: id, newTrackId: newId() });
          setChosen(only(id));
        };
  // Remove background on the selected video (spec remove-background.md), or Restore Original once
  // its background is removed; the clips whose run is on, marked on the timeline.
  const removing = new Set(
    draft.clips.flatMap((clip) => {
      const run = background.runOf(clip.id);
      return run?.state === "starting" || run?.state === "running" ? [clip.id] : [];
    }),
  );
  const restorable = selectedClip?.kind === "video" && selectedClip.originalSource !== null;
  const removeBackgroundSelected =
    changing?.kind !== "video" || removing.has(changing.id) ? undefined : () => background.remove(changing);
  const restoreSelected =
    changing?.kind !== "video" || changing.originalSource === null
      ? undefined
      : () => edit({ type: "restoreOriginal", id: changing.id });
  // Mute, Lock, Hide and Flip set every chosen clip the way the first one turns.
  const sounding = changeable.filter(
    (clip): clip is VideoClip | AudioClip => clip.kind === "video" || clip.kind === "audio",
  );
  const muteSelected = editAll(
    sounding.map((clip) => ({ type: "setClip", id: clip.id, patch: { muted: !sounding[0]!.muted } })),
  );
  const lockSelected =
    readOnly || selectedClip === undefined
      ? undefined
      : editAll(selectedClips.map((clip) => ({ type: "setLock", id: clip.id, locked: !selectedClip.locked })));
  // Live's canvas menu, made to work (the lead's decision on #267; live's four are inert): Hide,
  // Flip and Rotate 90° on the chosen pictures and texts, as their Parameters set them.
  const shown = changeable.filter(
    (clip): clip is VideoClip | ImageClip | TextClip => clip.kind !== "audio",
  );
  const hideSelected = editAll(
    shown.map((clip) => ({ type: "setClip", id: clip.id, patch: { hidden: !shown[0]!.hidden } })),
  );
  const pictures = changeable.filter(
    (clip): clip is VideoClip | ImageClip => clip.kind === "video" || clip.kind === "image",
  );
  const flipSelected = (axis: "flipX" | "flipY") =>
    editAll(pictures.map((clip) => ({ type: "setClip", id: clip.id, patch: { [axis]: !pictures[0]![axis] } })));
  const turned = (degrees: number) => (degrees + 90 > 360 ? degrees + 90 - 360 : degrees + 90);
  const rotateSelected = editAll(
    shown.map((clip) =>
      clip.kind === "text"
        ? { type: "setClip", id: clip.id, patch: { style: { rotation: turned(clip.style.rotation) } } }
        : { type: "setClip", id: clip.id, patch: { rotation: turned(clip.rotation) } },
    ),
  );
  // Live's clipboard: the editor's own, for clips (a page's text copies as a browser's does).
  const [clipboard, setClipboard] = useState<readonly Clip[]>([]);
  const copySelected =
    selectedClips.length === 0 || holdsCaption ? undefined : () => setClipboard(selectedClips);
  // A transition is not a clip: it is not cut to the clipboard.
  const removeChangeable = chosenTransition === undefined ? removeSelected : undefined;
  const cutSelected =
    removeChangeable === undefined || holdsCaption
      ? undefined
      : () => {
          setClipboard(selectedClips);
          removeChangeable();
        };
  const pasteAtPlayhead =
    readOnly || clipboard.length === 0
      ? undefined
      : () => {
          const newIds = clipboard.map(() => newId());
          const newTrackIds = clipboard.map(() => newId());
          edit({ type: "paste", clips: clipboard, atMs: playhead.get(), newIds, newTrackIds });
          const [first] = newIds;
          if (first !== undefined) setChosen({ id: first, ids: newIds });
        };
  // Live's ⌘A: every clip.
  const selectAll = () => setChosen((current) => everything(draft, current));
  // One frame of the render and the preview (spec E2): 30 a second.
  const frameMs = 1000 / 30;
  // Markers (spec markers): Add marker at the playhead, and M (D6) with no sound chosen, which takes
  // off the one within a frame of the playhead or adds one there; with a sound chosen, M is its
  // beats (item 21).
  const addMarker = readOnly ? undefined : () => edit({ type: "addMarker", id: newId(), atMs: playhead.get() });
  const markAtPlayhead =
    readOnly || selectedClips.some((clip) => clip.kind === "audio")
      ? undefined
      : () => {
          const atMs = playhead.get();
          const near = draft.markers.find((marker) => Math.abs(marker.atMs - atMs) <= frameMs);
          if (near === undefined) addMarker?.();
          else edit({ type: "removeMarker", id: near.id });
        };
  const commands: Record<Command, (() => void) | undefined> = {
    undo: readOnly ? undefined : undo,
    redo: readOnly ? undefined : redo,
    copy: copySelected,
    cut: cutSelected,
    paste: pasteAtPlayhead,
    remove: removeSelected,
    duplicate: duplicateSelected,
    split: splitSelected,
    separate: separateSelected,
    mute: muteSelected,
    lock: lockSelected,
    hide: hideSelected,
    selectAll,
    zoomIn: () => setZoom((current) => zoomBy(current, 1)),
    zoomOut: () => setZoom((current) => zoomBy(current, -1)),
    previousFrame: () => seek(Math.max(0, playhead.get() - frameMs)),
    nextFrame: () => seek(Math.min(total, playhead.get() + frameMs)),
    // The rows live greys out (spec capcut-shortcuts.md).
    previewAxis: togglePreviewAxis,
    snapping: toggleSnapping,
    moveTool: onCanvas("moveTool"),
    handTool: onCanvas("handTool"),
    zoomToFit: onCanvas("zoomToFit"),
    zoom50: onCanvas("zoom50"),
    zoom100: onCanvas("zoom100"),
    zoom200: onCanvas("zoom200"),
    canvasZoomIn: onCanvas("canvasZoomIn"),
    canvasZoomOut: onCanvas("canvasZoomOut"),
    fullScreen: onCanvas("fullScreen"),
    // The main track magnet's edits (spec magnet-ripple).
    rippleRemove: rippleSelected,
    trimStart: trimSelected("start"),
    trimEnd: trimSelected("end"),
    // M: a beat on a chosen sound (spec beats.md), else a marker at the playhead (spec markers.md).
    mark: markSelected ?? markAtPlayhead,
  };
  // Live's keys (the Shortcuts modal's table, `keys.ts`), on the window, except while typing in
  // a field (which keeps its own), inside a dialog or a menu, or for a key a block already took.
  const latest = useRef(commands);
  useEffect(() => {
    latest.current = commands;
  });
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest(`${TYPING}, [role="dialog"], [role="menu"]`) != null) return;
      const command = commandOf(event);
      if (command === null) return;
      // A page's selected text copies as text.
      if (command === "copy" && (window.getSelection()?.toString() ?? "") !== "") return;
      const run = latest.current[command];
      // The browser's own (zoom, bookmark, address bar) never runs for an editor key.
      event.preventDefault();
      run?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const mutedNow =
    (selectedClip?.kind === "video" || selectedClip?.kind === "audio") && selectedClip.muted;
  // Live's timeline menus: on a clip (which it also selects) or on the empty timeline.
  const [menu, setMenu] = useState<MenuPlace | null>(null);
  const menuGroups: (readonly MenuAction[])[] =
    menu?.clipId == null
      ? [[{ label: "Paste at Playhead", run: pasteAtPlayhead }]]
      : [
          [
            { label: "Copy", run: copySelected },
            { label: "Paste", run: pasteAtPlayhead },
            { label: "Duplicate", run: duplicateSelected },
          ],
          [
            { label: "Split at Playhead", run: splitSelected },
            { label: "Separate Audio", run: separateSelected },
            restorable
              ? { label: "Restore Original", run: restoreSelected }
              : { label: "Remove Background", run: removeBackgroundSelected },
          ],
          [
            { label: mutedNow ? "Unmute" : "Mute", run: muteSelected },
            { label: selectedClip?.locked === true ? "Unlock" : "Lock", run: lockSelected },
          ],
          [{ label: "Delete", run: removeSelected }],
        ];
  // Live's canvas menus: on a picture or a text (which it also selects), or on the empty canvas.
  const [canvasMenu, setCanvasMenu] = useState<MenuPlace | null>(null);
  const hiddenNow = selectedClip !== undefined && selectedClip.kind !== "audio" && selectedClip.hidden;
  const canvasGroups: (readonly MenuAction[])[] =
    canvasMenu?.clipId == null
      ? [[{ label: "Paste", run: pasteAtPlayhead }]]
      : [
          [
            { label: "Copy", run: copySelected },
            { label: "Paste", run: pasteAtPlayhead },
          ],
          [
            { label: hiddenNow ? "Show" : "Hide", run: hideSelected },
            { label: selectedClip?.locked === true ? "Unlock" : "Lock", run: lockSelected },
            {
              label: "Flip",
              run: undefined,
              items: [
                { label: "Flip Horizontal", run: flipSelected("flipX") },
                { label: "Flip Vertical", run: flipSelected("flipY") },
              ],
            },
            { label: "Rotate 90°", run: rotateSelected },
          ],
          [{ label: "Delete", run: removeSelected }],
        ];
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  // Built once, for the desktop's header and the phone's top bar alike.
  const nameField = (
    <input
      className={classes.projectName}
      aria-label="Video title"
      value={name}
      size={Math.max(8, name.length)}
      maxLength={PROJECT_NAME_MAX}
      readOnly={readOnly}
      spellCheck={false}
      onChange={(event) => {
        // A name typed is this tab's own edit: the autosave rests from it too.
        setEditedAt(Date.now());
        setTyped(event.currentTarget.value);
      }}
      onBlur={() => setTyped(savedName === saved.name ? null : savedName)}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
      }}
    />
  );
  const download = (
    <ExportPopover
      aspectRatio={draft.aspect}
      totalMs={total}
      // A save in flight spins it too: an export waits for the save to land.
      loading={busy}
      onExport={(settings) => void (busy ? undefined : exportDraft(settings))}
    >
      Download
    </ExportPopover>
  );
  const bellProps = {
    teamId,
    brandId,
    projectId: project.id,
    tasks: exports,
    local: local.exports,
    saves: fileSaves.saves,
    here: browserSaves.saves,
    onSaveAgain: fileSaves.retry,
    onSaveLocalAgain: local.saveAgain,
    onTryAgain: (task: ExportTask) => void saveToProject(task.folderId ?? null, true),
    canTryAgain: canSave,
    onClear: () => {
      local.clear();
      setExports((current) =>
        current.filter((task) => {
          const state = fileSaves.saves[task.jobId];
          return state === undefined || state.kind === "saving";
        }),
      );
    },
  };
  const saveState = readOnly
    ? "View only"
    : save.isPending
      ? "Saving…"
      : dirty
        ? "Unsaved changes"
        : "All changes saved";

  // The phone's sheets by panel: the media panel's own tabs, and the right panel's two.
  const binSheet: BinSheet | null =
    sheet === "media" || sheet === "text" || sheet === "captions" || sheet === "transitions" || sheet === "effects"
      ? sheet
      : null;
  const inspectorSheet = sheet === "assistant" || sheet === "parameters" ? sheet : null;
  // Parameters belong to a choice: cleared, their sheet shuts.
  if (sheet === "parameters" && selection === null) setSheet(null);
  const bell = bellState(bellProps, bellRead);
  const phoneTop = (
    <header className={classes.phoneTop}>
      <Link
        to={projectsIn(readPref("ui"))}
        params={{ teamId, brandId }}
        className={classes.phoneBack}
        aria-label="All projects"
      >
        <ChevronLeft size={22} />
      </Link>
      <Title order={1} className={classes.projectHeading} ref={heading} tabIndex={-1}>
        {nameField}
      </Title>
      <VisuallyHidden aria-live="polite">{saveState}</VisuallyHidden>
      <PhoneMenu
        onSave={readOnly || !canSave ? undefined : () => setSaveOpen(true)}
        onShortcuts={() => setShortcutsOpen(true)}
        onNotifications={() => openSheet("notifications")}
        notifications={bell.unseen > 0 ? `Notifications, ${bell.unseen} new` : "Notifications"}
        theme={scheme}
        onSnapshot={draft.clips.length > 0 ? () => void snapshot() : undefined}
        snapping={snapping}
        onSnapping={toggleSnapping}
        magnet={toggleMagnet === undefined ? undefined : { on: magnetOn, onToggle: toggleMagnet }}
      />
      {readOnly ? null : download}
    </header>
  );
  // The chosen's actions on a phone, each its desktop control's (spec phone-layout.md); More is the
  // timeline's right-click menu for the chosen.
  const onMenuFor = readOnly ? undefined : setMenu;
  const phoneRun = {
    // Clears the choice, so the tabs come back, with no edit.
    done: () => setChosen(null),
    split: splitSelected,
    duplicate: duplicateSelected,
    delete: removeSelected,
    speed: () => {
      openSheet("parameters");
      setSpeedShown((count) => count + 1);
    },
    volume: () => openSheet("parameters"),
    parameters: () => openSheet("parameters"),
    captions: () => openSheet("captions"),
    more:
      selection === null || onMenuFor === undefined
        ? undefined
        : (anchor: HTMLElement) => {
            const box = anchor.getBoundingClientRect();
            onMenuFor({ x: box.left + box.width / 2, y: box.top, clipId: selection.id });
          },
  };

  return (
    <div className={classes.studio} data-phone={phone || undefined}>
      {phone ? (
        phoneTop
      ) : (
      <header className={classes.header}>
        <div className={classes.headerStart}>
          <Link
            to={projectsIn(readPref("ui"))}
            params={{ teamId, brandId }}
            className={classes.logo}
            aria-label="All projects"
          >
            <Logo />
          </Link>
          <span className={classes.versionPill}>Beta</span>
          <HeaderTool label="Undo" shortcut="⌘Z" onClick={readOnly ? undefined : undo}>
            <Undo2 size={20} />
          </HeaderTool>
          <HeaderTool label="Redo" shortcut="⇧⌘Z" onClick={readOnly ? undefined : redo}>
            <Redo2 size={20} />
          </HeaderTool>
        </div>
        <div className={classes.headerCenter}>
          <Title order={1} className={classes.projectHeading} ref={heading} tabIndex={-1}>
            {/* Live's plain name field (`header.tsx`): typing renames; Enter or leaving keeps it,
                and an empty name goes back to the saved one. */}
            {nameField}
          </Title>
          {/* Live shows no save line (its Save button is the signal); a screen reader still
              hears where the draft stands. */}
          <VisuallyHidden aria-live="polite">{saveState}</VisuallyHidden>
        </div>
        <div className={classes.headerEnd}>
          <ThemeToggle />
          <Tooltip label="Keyboard shortcuts">
            <ActionIcon
              variant="subtle"
              color="gray"
              size={36}
              aria-label="Keyboard shortcuts"
              aria-haspopup="dialog"
              onClick={() => setShortcutsOpen(true)}
            >
              <Keyboard size={20} />
            </ActionIcon>
          </Tooltip>
          {readOnly ? (
            <Text size="sm" c="var(--cai-color-text-muted)">
              Its creator or an admin can change this project.
            </Text>
          ) : (
            <>
              <Button
                variant="default"
                className={classes.saveButton}
                leftSection={<Save size={16} />}
                disabled={!canSave}
                aria-haspopup="dialog"
                onClick={() => setSaveOpen(true)}
              >
                Save
              </Button>
              <ExportBell {...bellProps} />
              {/* Live's "Download" opens its export presets: the render saves the MP4 to the
                  computer, and to the Library. Always live's indigo; with nothing to export it
                  says so. */}
              {download}
            </>
          )}
        </div>
      </header>
      )}
      <SaveToProject
        teamId={teamId}
        brandId={brandId}
        opened={saveOpen}
        onClose={() => setSaveOpen(false)}
        onSave={(folderId) => void saveToProject(folderId)}
      />
      <div className={classes.notices}>
        {phone && readOnly ? (
          <Text size="sm" c="var(--cai-color-text-muted)">
            Its creator or an admin can change this project.
          </Text>
        ) : null}
        {/* A newer version than the draft names (spec conflict-keeps-draft): the draft and its
            undo stay. Keep mine only when there are changes of this tab's to keep. */}
        {theirs === null ? null : (
          <Alert color="yellow" variant="light" role="alert" title="Saved elsewhere">
            <Stack gap={8}>
              <Text size="sm">
                A newer version of this project was saved in another tab or by someone else.
                {dirty ? " Your changes are still here." : null}
              </Text>
              <Group gap={8}>
                {dirty && !readOnly ? (
                  // A draft the checks refuse waits for its fix, as the autosave does.
                  <Button size="compact-sm" onClick={() => void saveDraft(theirs.version)} disabled={busy || blocked.length > 0}>
                    Keep mine
                  </Button>
                ) : null}
                <Button size="compact-sm" variant="default" onClick={takeTheirs} disabled={busy}>
                  Take theirs
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}
        {dropped.length === 0 ? null : (
          <Alert color="yellow" variant="light" data-captions-dropped>
            {/* An <output> is a status region: a screen reader hears what the move took off. */}
            <Text component="output" size="sm">
              {droppedText(dropped)}
            </Text>
          </Alert>
        )}
        {projectQuery.isRefetchError ? <ErrorAlert error={projectQuery.error} /> : null}
        {tabs.others > 0 ? (
          <Text size="sm" c="var(--cai-color-text-muted)">
            Also open in another tab.
          </Text>
        ) : null}
        {blocked.map((problem) => (
          <Text key={problem} size="sm" c="var(--cai-color-text-danger)">
            {problem}
          </Text>
        ))}
        {saveProblem === null ? null : (
          <Stack gap={4}>
            <ErrorAlert error={saveProblem} />
            {/* The draft saves itself again with the next edit; this saves it now. */}
            <Group gap={8}>
              <Text size="sm">The changes are not saved yet.</Text>
              <Button size="compact-sm" variant="subtle" onClick={() => void saveDraft()} disabled={busy}>
                Try again
              </Button>
            </Group>
          </Stack>
        )}
        {exportProblem === null ? null : <ErrorAlert error={exportProblem} />}
        {refused === null ? null : (
          <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
            Export failed: {refused}
          </Text>
        )}
        {nothing && draft.clips.length === 0 ? (
          <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
            Export failed: No clips to export
          </Text>
        ) : null}
        {exports.map((task) => (
          <ExportSaver
            key={task.jobId}
            teamId={teamId}
            brandId={brandId}
            projectId={project.id}
            task={task}
            onDone={(jobId, url, name) =>
              task.kind === "save" ? fileSaves.kept(jobId) : fileSaves.start(jobId, url, name)
            }
            onFailed={fileSaves.fail}
          />
        ))}
        {newest === "local" && local.exports[0] !== undefined ? (
          <LocalExportStatus key={local.exports[0].id} task={local.exports[0]} />
        ) : null}
        {newest === "local" || exports[0] === undefined ? null : (
          <ExportStatus
            key={exports[0].jobId}
            teamId={teamId}
            brandId={brandId}
            projectId={project.id}
            jobId={exports[0].jobId}
            kept={exports[0].kind === "save"}
            save={fileSaves.saves[exports[0].jobId]}
            here={browserSaves.saves[exports[0].jobId]}
            handedOver={exports[0].handedOver === true}
            onRetry={(url) => fileSaves.retry(exports[0]!.jobId, url, bellName(exports[0]!.startedAt))}
          />
        )}
      </div>
      <div className={classes.workspace}>
        <div className={classes.panels}>
          <BottomSheet
            phone={phone}
            open={binSheet !== null}
            title={sheetTitle(binSheet ?? "media")}
            height={sheetHeight}
            onHeight={setSheetHeight}
            onClose={closeSheet}
          >
          <MediaBin
            teamId={teamId}
            brandId={brandId}
            full={fullKinds(draft)}
            readOnly={readOnly}
            onAdd={addClip}
            canAddTitle={!readOnly && countOf(draft, "text") < MAX_TITLES}
            onAddTitle={addTitle}
            onApplyTransition={applyTransition}
            onDragTransition={(kind) => setDroppingTransition(kind !== null)}
            transitionsOpened={transitionsOpened}
            canAddEffect={!readOnly && countOf(draft, "effect") < MAX_EFFECTS && draft.tracks.length < MAX_TRACKS}
            onAddEffect={addEffect}
            timeline={draft}
            aspectRatio={draft.aspect}
            playhead={playhead}
            onEdit={change}
            onSeek={seek}
            chosenCaption={chosenCaption}
            generation={captions}
            captionsSaid={captionsSaid}
            onCaptionsSay={setCaptionsSaid}
            phoneTab={phone ? binSheet : undefined}
          />
          </BottomSheet>
          <AtPlayhead store={playhead}>
            {(ms) => (
              <Preview
                timeline={draft}
                selection={selection}
                onPick={(id, more) => {
                  // Live's ⇧-click adds a clip to the chosen, or takes it out; a click chooses it alone.
                  if (id === null) {
                    if (!more) setChosen(null);
                  } else setChosen(more ? toggled(clipsChosen(selection, draft), id) : only(id));
                }}
                readOnly={readOnly}
                onEdit={change}
                onMenu={(place) => {
                  // A right-click on a chosen clip keeps the chosen; on another, chooses it alone.
                  if (place.clipId !== null && !isChosen(selection, place.clipId)) setChosen(only(place.clipId));
                  setCanvasMenu(place);
                }}
                aspectRatio={draft.aspect}
                onAspect={readOnly ? undefined : (aspect) => change({ type: "setAspect", aspect })}
                media={media}
                playheadMs={ms}
                playing={playing}
                onPlayhead={onPlayhead}
                onPlaying={onPlaying}
                hoverMs={previewAxis && !playing ? hoverMs : null}
                mac={mac}
                controls={canvas}
                touch={phone}
              />
            )}
          </AtPlayhead>
          <BottomSheet
            phone={phone}
            open={inspectorSheet !== null}
            title={sheetTitle(inspectorSheet ?? "assistant")}
            height={sheetHeight}
            onHeight={setSheetHeight}
            onClose={closeSheet}
          >
          <Inspector
            teamId={teamId}
            brandId={brandId}
            projectId={project.id}
            assistant={assistantEditor}
            timeline={draft}
            media={media}
            selection={selection}
            readOnly={readOnly}
            aspectRatio={draft.aspect}
            playhead={playhead}
            onSeek={seek}
            background={background}
            onEdit={change}
            phoneTab={phone ? inspectorSheet : undefined}
            speedShown={speedShown}
          />
          </BottomSheet>
        </div>
      </div>
      {phone ? (
        <AtPlayhead store={playhead}>
          {(ms) => (
            <PhoneTransport
              playing={playing}
              onPlay={draft.clips.length > 0 ? togglePlay : undefined}
              playheadMs={ms}
              totalMs={total}
              onUndo={undo}
              onRedo={redo}
              onFullScreen={fullScreenSupported() ? onCanvas("fullScreen") : undefined}
            />
          )}
        </AtPlayhead>
      ) : null}
      <section
        className={classes.timeline}
        data-folded={timelineFolded || undefined}
        style={timelineFolded || phone ? undefined : { height: timelineHeight }}
        aria-label="Timeline"
      >
        {/* Live's folded timeline has no handle. */}
        {timelineFolded || phone ? null : <TimelineResizer height={timelineHeight} onHeight={setTimelineHeight} />}
        {phone ? null : (
        <AtPlayhead store={playhead}>
          {(ms) => (
            <TimelineToolbar
              playing={playing}
              canPlay={draft.clips.length > 0}
              onPlay={togglePlay}
              onSeek={seek}
              playheadMs={ms}
              totalMs={total}
              onDelete={removeSelected}
              onSplit={splitSelected}
              onDuplicate={duplicateSelected}
              onSnapshot={() => void snapshot()}
              onAddMarker={addMarker}
              zoom={zoom}
              onZoom={setZoom}
              collapsed={timelineFolded}
              onCollapse={setTimelineFolded}
              switches={[
                { label: "Snapping", icon: <SeparatorVertical size={15} />, on: snapping, onToggle: toggleSnapping },
                { label: "Preview axis", icon: <ScanEye size={15} />, on: previewAxis, onToggle: togglePreviewAxis },
                // The main track magnet (spec magnet-ripple, D6): an edit, so only for who may edit.
                ...(toggleMagnet === undefined
                  ? []
                  : [{ label: "Main track magnet", icon: <Magnet size={15} />, on: magnetOn, onToggle: toggleMagnet }]),
              ]}
            />
          )}
        </AtPlayhead>
        )}
        {timelineFolded && !phone ? (
          // Live's folded timeline: its toolbar over a seek slider, 90px in all.
          <div className={classes.foldedSeek}>
            <AtPlayhead store={playhead}>
              {(ms) => (
                <Slider
                  thumbLabel="Playhead"
                  min={0}
                  max={Math.max(total, 1)}
                  step={10}
                  value={Math.min(ms, total)}
                  onChange={seek}
                  label={null}
                  size="sm"
                  disabled={draft.clips.length === 0}
                />
              )}
            </AtPlayhead>
          </div>
        ) : (
          <AtPlayhead store={playhead}>
            {(ms) => (
              <Tracks
                timeline={draft}
                media={media}
                selection={selection}
                playheadMs={ms}
                onSelect={setChosen}
                onEdit={change}
                onSeek={seek}
                onMenu={readOnly ? undefined : setMenu}
                readOnly={readOnly}
                pxPerSecond={PX_PER_SECOND * zoom}
                onAddTransition={readOnly ? undefined : addTransitionAt}
                droppingTransition={droppingTransition}
                onDropTransition={(cut, kind) => {
                  setDroppingTransition(false);
                  putTransition(cut, kind);
                }}
                onDropEffect={addEffect}
                onDropText={dropTitle}
                snapping={snapping}
                previewAxis={previewAxis}
                onHover={setHoverMs}
                touch={phone ? { zoom, onZoom: setZoom } : undefined}
                removing={removing}
              />
            )}
          </AtPlayhead>
        )}
        <TimelineMenu place={menu} groups={menuGroups} mac={mac} onClose={() => setMenu(null)} />
        <TimelineMenu
          place={canvasMenu}
          groups={canvasGroups}
          mac={mac}
          name={canvasMenu?.clipId == null ? "Canvas" : "Canvas object"}
          onClose={() => setCanvasMenu(null)}
        />
      </section>
      {phone ? (
        selection === null ? (
          <PhoneTabs open={sheet} onOpen={openSheet} />
        ) : (
          <PhoneActions actions={phoneActions(selectedClips.map((clip) => clip.kind), chosenTransition !== undefined)} run={phoneRun} />
        )
      ) : null}
      {phone ? (
        <BottomSheet
          phone
          open={sheet === "notifications"}
          title="Notifications"
          height={sheetHeight}
          onHeight={setSheetHeight}
          onClose={closeSheet}
        >
          <div className={classes.sheetList}>
            <ExportList {...bellProps} read={bellRead} onRead={setBellRead} titleId={null} />
          </div>
        </BottomSheet>
      ) : null}
      {phone && sheet !== null ? (
        // Behind the open sheet: a tap anywhere else closes it. The sheet's own close, and Escape,
        // are the keys' way, so the dim is no target of theirs.
        <button type="button" className={classes.sheetDim} tabIndex={-1} aria-hidden onClick={closeSheet} />
      ) : null}
      <ShortcutsModal opened={shortcutsOpen} onClose={() => setShortcutsOpen(false)} mac={mac} />
      <ConfirmModal
        opened={leaving.status === "blocked"}
        title="Leave without saving?"
        confirmLabel="Leave"
        onConfirm={() => leaving.proceed?.()}
        onCancel={() => leaving.reset?.()}
      >
        <Text size="sm">Your changes to {readName} are not saved yet.</Text>
      </ConfirmModal>
    </div>
  );
});

/** A header icon of live's that works here: its name and shortcut in a tooltip, off when idle. */
function HeaderTool(props: {
  label: string;
  shortcut: string;
  onClick: (() => void) | undefined;
  children: ReactNode;
}) {
  return (
    <Tooltip label={`${props.label} (${props.shortcut})`}>
      <ActionIcon
        variant="subtle"
        color="gray"
        size={36}
        aria-label={props.label}
        disabled={props.onClick === undefined}
        onClick={props.onClick}
      >
        {props.children}
      </ActionIcon>
    </Tooltip>
  );
}

/** The app's light and dark switch, as live's editor has it in its header. */
function ThemeToggle() {
  const { Icon, label, flip } = useSchemeSwitch();
  return (
    <Tooltip label={label}>
      <ActionIcon variant="subtle" color="gray" size={36} aria-label={label} onClick={flip}>
        <Icon size={18} />
      </ActionIcon>
    </Tooltip>
  );
}

/** Whether two export settings ask for the same file. */
function sameSettings(one: ExportSettings, other: ExportSettings): boolean {
  return JSON.stringify(one) === JSON.stringify(other);
}
